import React from "react";
import { StaffRole, TeamMember } from "../api/contentful";
import LoadingSpinner from "./LoadingSpinner";
import "./TeamSection.css";

interface TeamSectionProps {
  isLoadingStaffSection: boolean;
  staffSectionError: string | null;
  staffRoles: StaffRole[] | null;
  teamMembersByRoleId: Map<string, TeamMember[]>;
}

const MEMBERS_PER_ROW = 2;

function sortMembers(members: TeamMember[]): TeamMember[] {
  return [...members].sort((a, b) => {
    const aFull = `${a.surname || ""} ${a.name || ""}`.trim().toLowerCase();
    const bFull = `${b.surname || ""} ${b.name || ""}`.trim().toLowerCase();
    return aFull.localeCompare(bFull);
  });
}

function formatMemberYears(
  startDate?: string,
  endDate?: string,
): string | null {
  const startYear = startDate ? new Date(startDate).getFullYear() : null;
  const endYear = endDate ? new Date(endDate).getFullYear() : null;

  if (!startYear && !endYear) return null;
  if (startYear && endYear) return `${startYear}–${endYear}`;
  if (startYear) return `Since ${startYear}`;
  return String(endYear);
}

function buildMemberMeta(member: TeamMember): string {
  return [
    formatMemberYears(member.startDate, member.endDate),
    member.academicInstitution,
    member.studyField,
  ]
    .filter(Boolean)
    .join(" · ");
}

function chunkMembers<T>(members: T[], rowSize: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < members.length; i += rowSize) {
    rows.push(members.slice(i, i + rowSize));
  }
  return rows;
}

function TeamMemberCard({ member }: { member: TeamMember }) {
  const fullName = `${member.name} ${member.surname}`.trim();
  const meta = buildMemberMeta(member);

  return (
    <li className="team-section__member">
      <img
        className="team-section__avatar"
        src={member.profilePicture}
        alt=""
        loading="lazy"
      />
      <div className="team-section__member-info">
        <p className="team-section__name">
          {fullName}
          {member.pronouns?.trim() && (
            <span className="team-section__pronouns">
              {" "}
              ({member.pronouns.trim()})
            </span>
          )}
        </p>
        {member.staffTitle && (
          <p className="team-section__position">{member.staffTitle}</p>
        )}
        {meta && <p className="team-section__meta">{meta}</p>}
      </div>
    </li>
  );
}

const TeamSection: React.FC<TeamSectionProps> = ({
  isLoadingStaffSection,
  staffSectionError,
  staffRoles,
  teamMembersByRoleId,
}) => {
  return (
    <section className="team-section" aria-labelledby="our-team-heading">
      <h3 id="our-team-heading" className="team-section__heading">
        Our Amazing Team
      </h3>

      {isLoadingStaffSection && <LoadingSpinner />}

      {!isLoadingStaffSection && staffSectionError && (
        <p className="team-section__message">Error: {staffSectionError}</p>
      )}

      {!isLoadingStaffSection &&
        !staffSectionError &&
        (!staffRoles || staffRoles.length === 0) && (
          <p className="team-section__message">No staff roles available.</p>
        )}

      {!isLoadingStaffSection &&
        !staffSectionError &&
        staffRoles &&
        staffRoles.length > 0 && (
          <div className="team-section__roles">
            {staffRoles.map((role: StaffRole) => {
              const sortedMembers = sortMembers(
                teamMembersByRoleId.get(role.contentfulId) || [],
              );
              if (sortedMembers.length === 0) return null;

              return (
                <section
                  key={role.contentfulId}
                  className="team-section__role"
                  aria-labelledby={`team-role-${role.contentfulId}`}
                >
                  <header className="team-section__role-header">
                    <h4
                      className="team-section__role-title"
                      id={`team-role-${role.contentfulId}`}
                    >
                      {role.title}
                    </h4>
                    {role.fullDescription && (
                      <p className="team-section__role-description">
                        {role.fullDescription}
                      </p>
                    )}
                  </header>

                  <ul className="team-section__members">
                    {chunkMembers(sortedMembers, MEMBERS_PER_ROW).map(
                      (rowMembers, rowIndex) => (
                        <li
                          key={`${role.contentfulId}-row-${rowIndex}`}
                          className="team-section__row"
                        >
                          <ul className="team-section__row-grid">
                            {rowMembers.map((member) => (
                              <TeamMemberCard
                                key={member.contentfulId}
                                member={member}
                              />
                            ))}
                          </ul>
                        </li>
                      ),
                    )}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
    </section>
  );
};

export default TeamSection;
